import { useMemo, useState, useCallback } from 'react';
import moment from 'utils/moment';
import { toast } from 'react-toastify';
import { useAppDispatch } from 'store/hooks';
import shopActions from 'services/actions/shop';
import shopSelectors from 'services/selectors/shop';
import apis from 'services/apis';
import { useCalendarTimeOffSync } from './use-calendar-sync';
import { ANYONE_ID } from 'Calendar/anyone';
import { adaptBlockHourToTimeOff, ScheduleMap } from '../services/time-off-adapter';
import type { ITimeOffEntry } from '../services/types';

const DATE_KEY = 'YYYY-MM-DD';

/** Days ahead of today covered by the default range. */
export const DEFAULT_RANGE_DAYS = 7;

const todayKey = () => moment().format(DATE_KEY);
const defaultRangeEnd = () => moment().add(DEFAULT_RANGE_DAYS, 'days').format(DATE_KEY);

/** An entry belongs to the range when the two (inclusive) spans intersect. */
const overlapsRange = (e: ITimeOffEntry, start: string, end: string) => e.start <= end && e.end >= start;

/**
 * Time Off List data. The list is scoped to a DATE RANGE which is also the query
 * range (`startTime`/`endTime`). It opens on today → today + 7 days; picking a
 * start day and then an end day refetches for the new range.
 */
export const useTimeOffList = () => {
  const dispatch = useAppDispatch();
  const syncCalendar = useCalendarTimeOffSync();
  const rawList = shopSelectors.timeOffList() || [];
  const loading = shopSelectors.timeOffListLoading();
  const staffsAvailable = shopSelectors.pureStaffs();
  const schedule = shopSelectors.scheduleBooking() as ScheduleMap;

  // "Anyone" is a booking placeholder, not a real staff member, and cannot hold
  // block hours — exclude it from the strip.
  const staffs = useMemo(
    () => (staffsAvailable || []).filter(s => String(s.id) !== ANYONE_ID),
    [staffsAvailable],
  );

  const now = moment();
  const [viewYear, setViewYear] = useState<number>(now.year());
  const [viewMonth, setViewMonth] = useState<number>(now.month()); // 0-indexed
  const [selectedStaffIds, setSelectedStaffIds] = useState<number[]>([]);
  const [rangeStart, setRangeStart] = useState<string>(todayKey);
  const [rangeEnd, setRangeEnd] = useState<string>(defaultRangeEnd);
  // 'start' = next tap begins a new range, 'end' = next tap closes it.
  const [pickPhase, setPickPhase] = useState<'start' | 'end'>('start');
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);

  // Staff lookup: name, photo, and strip index (gradient fallback + row sort order).
  const { nameById, indexById, avatarById } = useMemo(() => {
    const nameMap: Record<number, string> = {};
    const idxMap: Record<number, number> = {};
    const avatarMap: Record<number, string> = {};
    staffs.forEach((s, i) => {
      nameMap[Number(s.id)] = s.name;
      idxMap[Number(s.id)] = i;
      avatarMap[Number(s.id)] = s.avatar;
    });
    return { nameById: nameMap, indexById: idxMap, avatarById: avatarMap };
  }, [staffs]);

  const entries = useMemo<ITimeOffEntry[]>(
    () => rawList.filter(item => !!nameById[item.staffId]).map(item => adaptBlockHourToTimeOff(item, { staffName: nameById[item.staffId], schedule })),
    [rawList, nameById, schedule],
  );

  const orderOf = useCallback((staffId: number) => (indexById[staffId] ?? Number.MAX_SAFE_INTEGER), [indexById]);

  const isSelected = useCallback(
    (staffId: number) => selectedStaffIds.length === 0 || selectedStaffIds.includes(staffId),
    [selectedStaffIds],
  );

  /** Fetch a date range: the selected range IS the query range. */
  const fetchRange = useCallback((start: string, end: string) => {
    dispatch(shopActions.timeOffList.fetch({ startTime: start, endTime: end }));
  }, [dispatch]);

  // --- filters ---
  const toggleStaff = useCallback((staffId: number) => {
    setPendingDeleteId(null);
    setSelectedStaffIds(prev => (prev.includes(staffId) ? prev.filter(x => x !== staffId) : [...prev, staffId]));
  }, []);
  // Empty selection means "all staff", so Clear is the only reset needed.
  const clearStaff = useCallback(() => { setSelectedStaffIds([]); setPendingDeleteId(null); }, []);

  /**
   * Two-tap range pick. The first tap anchors the range on one day; the second
   * closes it (swapping when the second day is earlier). The fetch is driven by
   * the range effect in the modal, so both taps refetch for the range shown.
   */
  const pickDay = useCallback((dayKey: string) => {
    setPendingDeleteId(null);
    if (pickPhase === 'start') {
      setRangeStart(dayKey);
      setRangeEnd(dayKey);
      setPickPhase('end');
      return;
    }
    if (dayKey < rangeStart) {
      setRangeEnd(rangeStart);
      setRangeStart(dayKey);
    } else {
      setRangeEnd(dayKey);
    }
    setPickPhase('start');
  }, [pickPhase, rangeStart]);

  // Compute year+month together from a single source to avoid a stale-closure
  // year/month mismatch across a year boundary on rapid nav. Month nav only
  // moves the picker; it does not fetch until a day is picked.
  const goMonth = useCallback((y: number, m: number) => { setViewYear(y); setViewMonth(m); }, []);
  const prevMonth = useCallback(
    () => goMonth(viewMonth === 0 ? viewYear - 1 : viewYear, viewMonth === 0 ? 11 : viewMonth - 1),
    [goMonth, viewYear, viewMonth],
  );
  const nextMonth = useCallback(
    () => goMonth(viewMonth === 11 ? viewYear + 1 : viewYear, viewMonth === 11 ? 0 : viewMonth + 1),
    [goMonth, viewYear, viewMonth],
  );

  /** Reset the picker and the range to the default today → +7 days (used on open). */
  const goToday = useCallback(() => {
    const t = moment();
    setViewYear(t.year());
    setViewMonth(t.month());
    setRangeStart(t.format(DATE_KEY));
    setRangeEnd(t.clone().add(DEFAULT_RANGE_DAYS, 'days').format(DATE_KEY));
    setPickPhase('start');
    setPendingDeleteId(null);
  }, []);

  // --- derived ---
  // The fetch already returns the range, but entries are re-checked here so the
  // table never shows rows outside the range shown in the calendar.
  const filteredEntries = useMemo(() => {
    const rows = entries.filter(e => isSelected(e.staffId) && overlapsRange(e, rangeStart, rangeEnd));
    return [...rows].sort((a, b) => (a.start === b.start ? orderOf(a.staffId) - orderOf(b.staffId) : a.start < b.start ? -1 : 1));
  }, [entries, isSelected, rangeStart, rangeEnd, orderOf]);

  // Per-staff badge counts for the range (ignore the staff filter).
  const perStaffCounts = useMemo(() => {
    const counts: Record<number, number> = {};
    entries.forEach(e => {
      if (!overlapsRange(e, rangeStart, rangeEnd)) return;
      counts[e.staffId] = (counts[e.staffId] || 0) + 1;
    });
    return counts;
  }, [entries, rangeStart, rangeEnd]);

  const deleteEntry = useCallback(async (id: number) => {
    try {
      await apis.deleteBlockHours(String(id));
      // Keep the outer scheduler calendar (block hours + staff columns) in sync.
      syncCalendar();
    } catch (e) {
      toast.error('Failed to delete time off');
    } finally {
      setPendingDeleteId(null);
      fetchRange(rangeStart, rangeEnd);
    }
  }, [syncCalendar, fetchRange, rangeStart, rangeEnd]);

  return {
    staffs,
    indexById,
    avatarById,
    entries,
    filteredEntries,
    perStaffCounts,
    loading,
    // filter state
    selectedStaffIds,
    rangeStart,
    rangeEnd,
    pickPhase,
    viewYear,
    viewMonth,
    isSelected,
    // actions
    fetchRange,
    goToday,
    toggleStaff,
    clearStaff,
    pickDay,
    prevMonth,
    nextMonth,
    // delete
    pendingDeleteId,
    setPendingDeleteId,
    deleteEntry,
  };
};

export type UseTimeOffList = ReturnType<typeof useTimeOffList>;
